export interface UserMinimal {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

export interface AreaTagSetMinimal {
  id: number;
  name: string;
}

export interface LocationMinimal {
  id: number;
  name: string;
  layout_id?: number;
  layout_name?: string | null;
  area_tag_sets?: AreaTagSetMinimal[];
}

export interface WorkflowAreaTagLocation extends LocationMinimal {
  photo_ids: number[];
}

/**
 * A unit/location label, prefixed with its layout (building) name when known —
 * e.g. "Building A - 107" — since unit names repeat across buildings.
 */
export const formatUnitLabel = (
  location?: { name: string; layout_name?: string | null } | null
): string => {
  if (!location) return '—';
  return location.layout_name ? `${location.layout_name} - ${location.name}` : location.name;
};

export interface PunchListItemType {
  id: string;
  name: string;
}

export interface Trade {
  id: string;
  name: string;
  color?: string;
  icon?: string;
}

export interface TradePartnerMinimal {
  id: number;
  company_name: string;
  trade: Trade | null;
}

export interface CostCode {
  id: string;
  name: string;
  code: string;
}

export type PunchListPriority = 'LOW' | 'MEDIUM' | 'HIGH';
export type PunchListStatus = 'OPEN' | 'PENDING_APPROVAL' | 'COMPLETED' | 'OVERDUE' | 'CLOSED';
export type ImpactChoice = 'YES' | 'YES_UNKNOWN' | 'NO' | 'TBD' | 'NA';
export type PunchListAttachmentKind = 'general' | 'before' | 'after' | 'proof';
export type { PunchWalkthroughStatus } from '@ironview-360/api-client/walkthroughs';
export type PunchListsSortKey =
  | 'status'
  | 'priority'
  | 'item_type'
  | 'location'
  | 'due_date'
  | 'created_at';

export interface PunchListsSortState {
  key: PunchListsSortKey;
  direction: 'asc' | 'desc';
}

export interface PunchListsAdvancedSortState {
  rules: PunchListsSortState[];
}

export type PunchListsSavedSortState = PunchListsSortState | PunchListsAdvancedSortState;

export interface PunchListAttachment {
  id: string;
  item: string;
  kind: PunchListAttachmentKind;
  file: string;
  file_url: string | null;
  uploaded_by: UserMinimal | null;
  created_at: string;
}

export interface PunchListReportImage {
  attachment_id: string;
  item_id: string;
  url: string;
  file_name: string;
  kind: PunchListAttachmentKind;
}

export interface HotspotDetail {
  id: number;
  pitch: number;
  yaw: number;
  hfov: number;
  photo_id: number;
  scan_type?: 'Progress' | 'Asbuild' | 'Corridor';
}

/** Full detail shape returned by the retrieve endpoint */
export interface PunchListItem {
  id: string;
  title: string;
  project: number;
  status: PunchListStatus;
  priority: PunchListPriority;
  punch_manager: UserMinimal | null;
  assignee: UserMinimal | null;
  final_approver: UserMinimal | null;
  created_by: UserMinimal | null;
  location: LocationMinimal | null;
  item_type: PunchListItemType | null;
  due_date: string | null;
  distribution_list: UserMinimal[];
  trade: Trade | null;
  responsible_trade_partner: TradePartnerMinimal | null;
  cost_impact: ImpactChoice;
  schedule_impact: ImpactChoice;
  cost_code: CostCode | null;
  is_private: boolean;
  description: string;
  attachments: PunchListAttachment[];
  hotspot_detail: HotspotDetail | null;
  completed_at: string | null;
  completed_by: UserMinimal | null;
  completion_certifier_name: string;
  submitted_company: { id: number; name: string } | null;
  can_update_status: boolean;
  can_edit: boolean;
  can_delete: boolean;
  workflow_run?: string | null;
  workflow_stage?: number | null;
  created_at: string;
  updated_at: string;
}

/** Slim shape used in list/table rows */
export interface PunchListItemSummary {
  id: string;
  title: string;
  description: string;
  status: PunchListStatus;
  priority: PunchListPriority;
  assignee: UserMinimal | null;
  punch_manager: UserMinimal | null;
  final_approver: UserMinimal | null;
  distribution_list: UserMinimal[];
  created_by: UserMinimal | null;
  due_date: string | null;
  location: LocationMinimal | null;
  item_type: PunchListItemType | null;
  trade: Trade | null;
  responsible_trade_partner: TradePartnerMinimal | null;
  cost_impact: ImpactChoice;
  schedule_impact: ImpactChoice;
  is_private: boolean;
  created_at: string;
  completed_at: string | null;
  completed_by: UserMinimal | null;
  completion_certifier_name: string;
  hotspot_id: number | null;
  workflow_run?: string | null;
  workflow_stage?: number | null;
  workflow_step_key?: string | null;
  can_edit: boolean;
  can_delete: boolean;
}

/** Payload for create / update */
export interface CreatePunchListItemData {
  title: string;
  project: number;
  status?: PunchListStatus;
  priority?: PunchListPriority;
  punch_manager?: number | null;
  assignee?: number | null;
  final_approver?: number | null;
  location?: number | null;
  item_type?: string | null;
  due_date?: string | null;
  distribution_list?: number[];
  trade?: string | null;
  responsible_trade_partner?: number | null;
  cost_impact?: ImpactChoice;
  schedule_impact?: ImpactChoice;
  cost_code?: string | null;
  is_private?: boolean;
  description?: string;
  hotspot?: number | null;
}

export type UpdatePunchListItemData = Partial<CreatePunchListItemData>;

export interface PunchListItemFilters {
  status?: PunchListStatus;
  priority?: PunchListPriority;
  assignee?: number;
  assignee_unassigned?: boolean;
  trade_id?: string;
  item_type_id?: string;
  location_id?: number;
  due_state?: 'overdue' | 'due_today' | 'due_next_7' | 'no_due_date';
  visibility?: 'private' | 'shared';
  hotspot_state?: 'linked' | 'unlinked';
  due_from?: string;
  due_to?: string;
  created_from?: string;
  created_to?: string;
  search?: string;
  hotspot_id?: number;
  photo_id?: number;
  area_tag_id?: number;
  area_tag_set_id?: number;
  layout_id?: number;
}

export interface PunchListFilterView {
  id: string;
  name: string;
  description: string;
  project: number;
  client: number;
  created_by: UserMinimal;
  filters: PunchListItemFilters;
  sort_state: PunchListsSavedSortState | null;
  created_at: string;
  updated_at: string;
}

export interface CreatePunchListFilterViewData {
  name: string;
  description?: string;
  project: number;
  filters: PunchListItemFilters;
  sort_state?: PunchListsSavedSortState | null;
}

export interface PunchListItemsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PunchListItemSummary[];
}

export type PunchListCsvColumnKey =
  | 'title'
  | 'description'
  | 'status'
  | 'priority'
  | 'assignee'
  | 'punch_manager'
  | 'final_approver'
  | 'distribution_list'
  | 'trade'
  | 'responsible_trade_partner'
  | 'item_type'
  | 'location'
  | 'due_date'
  | 'cost_impact'
  | 'schedule_impact'
  | 'punch_item_link'
  | 'created_by'
  | 'created_at'
  | 'completed_at'
  | 'completed_by';

export const DEFAULT_PUNCH_LIST_CSV_COLUMN_KEYS: PunchListCsvColumnKey[] = [
  'title',
  'description',
  'status',
  'trade',
  'item_type',
  'location',
  'created_at',
  'completed_at',
];

export interface PunchListCsvColumn {
  key: PunchListCsvColumnKey;
  label: string;
}

export interface PunchListExportPreferences {
  available_columns: PunchListCsvColumn[];
  selected_columns: PunchListCsvColumnKey[];
}

export type QuickPunchFieldKey =
  | 'description'
  | 'title'
  | 'priority'
  | 'item_type'
  | 'trade'
  | 'responsible_trade_partner'
  | 'assignee'
  | 'punch_manager'
  | 'final_approver'
  | 'due_date'
  | 'cost_impact'
  | 'schedule_impact'
  | 'cost_code'
  | 'is_private';

export type QuickPunchDefaultValue = string | number | boolean | null;

export interface QuickPunchFieldDefinition {
  key: QuickPunchFieldKey;
  label: string;
  kind:
    | 'textarea'
    | 'text'
    | 'priority'
    | 'item_type'
    | 'trade'
    | 'trade_partner'
    | 'project_user'
    | 'datetime'
    | 'impact'
    | 'cost_code'
    | 'boolean';
  supports_default: boolean;
  system_required: boolean;
}

export interface QuickPunchPreference {
  visible_fields: QuickPunchFieldKey[];
  required_fields: QuickPunchFieldKey[];
  defaults: Partial<Record<QuickPunchFieldKey, QuickPunchDefaultValue>>;
}

export interface QuickPunchProjectConfiguration {
  required_fields: QuickPunchFieldKey[];
  defaults: Partial<Record<QuickPunchFieldKey, QuickPunchDefaultValue>>;
  updated_at: string | null;
}

export interface QuickPunchConfigurationResponse {
  available_fields: QuickPunchFieldDefinition[];
  user_preference: QuickPunchPreference | null;
  project_configuration: QuickPunchProjectConfiguration;
  effective: {
    visible_fields: QuickPunchFieldKey[];
    required_fields: QuickPunchFieldKey[];
    locked_fields: QuickPunchFieldKey[];
    defaults: Partial<Record<QuickPunchFieldKey, QuickPunchDefaultValue>>;
  };
}

export interface PunchListStatusSummaryEntry {
  status: PunchListStatus;
  count: number;
}

export type {
  PunchWalkthroughDraft, PunchWalkthroughSession, PunchWalkthroughDraftUpdate,
  PunchWalkthroughUploadInit as PunchWalkthroughUploadResponse, PunchWalkthroughSummary,
} from '@ironview-360/api-client/walkthroughs';

export interface SendPunchListRemindersData {
  project_id: number;
  item_ids: string[];
}

export interface SendPunchListRemindersResponse {
  success: boolean;
  item_count: number;
  recipient_count: number;
}

export interface ShareUnitPunchListReportsData {
  project_id: number;
  area_tag_ids: number[];
  recipient_emails: string[];
  message?: string;
}

export interface ShareUnitPunchListReportsResponse {
  success: boolean;
  unit_count: number;
  recipient_count: number;
  email_count: number;
}

export type PunchWorkflowRunStatus = 'ACTIVE' | 'COMPLETED';

export interface PunchItemWorkflowStep {
  id: string;
  stage: number;
  position: number;
  title: string;
  description: string;
  trade: string | null;
  trade_name: string | null;
  responsible_trade_partner: number | null;
  responsible_trade_partner_name: string | null;
  priority: PunchListPriority;
  due_days_after_activation: number;
}

export interface PunchItemWorkflow {
  id: string;
  project: number;
  client: number;
  name: string;
  description: string;
  is_template: boolean;
  steps: PunchItemWorkflowStep[];
  stage_count: number;
  created_by: UserMinimal | null;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

export interface PunchItemWorkflowStepInput {
  stage: number;
  position: number;
  title: string;
  description?: string;
  trade?: string | null;
  responsible_trade_partner?: number | null;
  priority?: PunchListPriority;
  due_days_after_activation?: number;
}

export interface CreatePunchItemWorkflowData {
  project: number;
  name: string;
  description?: string;
  is_template: boolean;
  steps: PunchItemWorkflowStepInput[];
}

export interface PunchWorkflowRun {
  id: string;
  workflow: string | null;
  batch: string | null;
  workflow_name: string;
  project: number;
  status: PunchWorkflowRunStatus;
  current_stage: number;
  total_stages: number;
  total_steps: number;
  completed_steps: number;
  definition: Array<{
    step_key: string;
    stage: number;
    position: number;
    title: string;
    description: string;
    trade: string | null;
    responsible_trade_partner: number | null;
    priority: PunchListPriority;
    due_days_after_activation: number;
  }>;
  location: LocationMinimal | null;
  hotspot: HotspotDetail | null;
  items: Array<PunchListItemSummary & { attachments: PunchListAttachment[] }>;
  created_by: UserMinimal | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

interface PunchWorkflowLaunchDefaults {
  location?: number | null;
  hotspot?: number | null;
  punch_manager?: number | null;
  final_approver?: number | null;
  distribution_list?: number[];
  item_type?: string | null;
  cost_code?: string | null;
  cost_impact?: ImpactChoice;
  schedule_impact?: ImpactChoice;
  is_private?: boolean;
}

export type CreatePunchWorkflowRunData = PunchWorkflowLaunchDefaults &
  (
    | { workflow: string; name?: never; steps?: never }
    | {
        workflow?: null;
        name: string;
        description?: string;
        is_template?: boolean;
        steps: PunchItemWorkflowStepInput[];
      }
  );

export type AddPunchWorkflowBatchRunData = PunchWorkflowLaunchDefaults &
  ({ hotspot: number; location?: never } | { location: number; hotspot?: never });

export interface PunchWorkflowBatch {
  id: string;
  workflow: string | null;
  name: string;
  project: number;
  status: PunchWorkflowRunStatus;
  current_stage: number;
  total_stages: number;
  total_runs: number;
  total_steps: number;
  completed_steps: number;
  runs: PunchWorkflowRun[];
  created_by: UserMinimal | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

interface PunchWorkflowBatchDefaults
  extends Omit<PunchWorkflowLaunchDefaults, 'location' | 'hotspot'> {
  name?: string;
}

type PunchWorkflowBatchTarget =
  | { locations: number[]; hotspot?: never }
  | { hotspot: number; locations?: never };

export type CreatePunchWorkflowBatchData = PunchWorkflowBatchDefaults &
  PunchWorkflowBatchTarget &
  (
    | {
        workflow: string;
        workflow_name?: never;
        workflow_description?: never;
        is_template?: never;
        steps?: never;
      }
    | {
        workflow?: null;
        workflow_name: string;
        workflow_description?: string;
        is_template?: boolean;
        steps: PunchItemWorkflowStepInput[];
      }
  );

export const PRIORITY_LABELS: Record<PunchListPriority, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
};

export const STATUS_LABELS: Record<PunchListStatus, string> = {
  OPEN: 'Open',
  PENDING_APPROVAL: 'Pending Approval',
  COMPLETED: 'Completed',
  OVERDUE: 'Overdue',
  CLOSED: 'Closed',
};

export const IMPACT_LABELS: Record<ImpactChoice, string> = {
  YES: 'Yes',
  YES_UNKNOWN: 'Yes (Unknown)',
  NO: 'No',
  TBD: 'TBD',
  NA: 'N/A',
};
